import React, { useState, useRef, useEffect } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import Upload from 'lucide-react/dist/esm/icons/upload';
import File from 'lucide-react/dist/esm/icons/file';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import { MyFile, FileUploadOptions, myFilesService } from '../../lib/myFilesService';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, options: FileUploadOptions) => Promise<void>;
  uploading: boolean;
  uploadProgress: number;
  currentPath: string;
}

export function FileUploadModal({
  isOpen,
  onClose,
  onUpload,
  uploading,
  uploadProgress,
  currentPath
}: FileUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [category, setCategory] = useState('custom');
  const [error, setError] = useState<string | null>(null);
  const [storageUsage, setStorageUsage] = useState<{
    totalUsed: number;
    maxStorage: number;
    percentageUsed: number;
  } | null>(null);

  // Load storage usage when modal opens
  useEffect(() => {
    if (isOpen) {
      loadStorageUsage();
    }
  }, [isOpen]);

  const loadStorageUsage = async () => {
    try {
      const usage = await myFilesService.getStorageUsage();
      setStorageUsage(usage);
    } catch (err) {
      console.error('Failed to load storage usage:', err);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (15GB limit per file)
    if (file.size > 15 * 1024 * 1024 * 1024) {
      setError('File size must be less than 15GB');
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setError(null);
      
      // Check storage limit before uploading
      const canUpload = await myFilesService.canUploadFile(selectedFile.size);
      if (!canUpload.canUpload) {
        setError(`Storage limit exceeded. You have used ${formatFileSize(canUpload.currentUsage)} of ${formatFileSize(canUpload.maxStorage)}. You need ${formatFileSize(selectedFile.size - canUpload.remainingSpace)} more space.`);
        return;
      }
      
      const options: FileUploadOptions = {
        folder: currentPath,
        description: description || undefined,
        tags: tags ? tags.split(',').map(tag => tag.trim()) : undefined,
        category: category as any,
      };

      await onUpload(selectedFile, options);
      
      // Reset form
      setSelectedFile(null);
      setDescription('');
      setTags('');
      setCategory('custom');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setDescription('');
      setTags('');
      setCategory('custom');
      setError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-idecide-dark">Upload File</h2>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle size={16} className="text-red-500" />
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* File Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select File
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-idecide-primary transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              disabled={uploading}
              className="hidden"
              accept="*"
            />
            <Upload size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600 mb-2">
              Click to select a file or drag and drop
            </p>
            <p className="text-xs text-gray-500">
              Max file size: 15GB
            </p>
            {storageUsage && (
              <div className="mt-2 text-xs text-gray-500">
                <div className="flex justify-between items-center">
                  <span>Storage used:</span>
                  <span>{formatFileSize(storageUsage.totalUsed)} / {formatFileSize(storageUsage.maxStorage)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                  <div 
                    className={`h-1 rounded-full ${
                      storageUsage.percentageUsed > 90 ? 'bg-red-500' : 
                      storageUsage.percentageUsed > 75 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(storageUsage.percentageUsed, 100)}%` }}
                  ></div>
                </div>
                <div className="text-center mt-1">
                  {storageUsage.percentageUsed.toFixed(1)}% used
                </div>
              </div>
            )}
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="mt-2"
            >
              Choose File
            </Button>
          </div>
        </div>

        {/* Selected File Info */}
        {selectedFile && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <File size={20} className="text-idecide-primary" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-gray-900 truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(selectedFile.size)} • {selectedFile.type}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* File Metadata */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              placeholder="Enter file description..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (optional)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              disabled={uploading}
              placeholder="Enter tags separated by commas..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent disabled:opacity-50"
            >
              <option value="custom">Custom</option>
              <option value="legal">Legal</option>
              <option value="financial">Financial</option>
              <option value="health">Health</option>
              <option value="pets">Pets</option>
              <option value="travel">Travel</option>
              <option value="digital">Digital</option>
              <option value="legacy">Legacy</option>
              <option value="ndis">NDIS</option>
            </select>
          </div>
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-idecide-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleClose}
            disabled={uploading}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="flex-1 bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </div>
      </Card>
    </div>
  );
} 